import { createTheme, ThemeProvider } from '@mui/system';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import darkTheme from '../themes/darkTheme';
import frostDarkTheme from '../themes/frostDarkTheme';
import lightTheme from '../themes/lightTheme';
import frostLightTheme from '../themes/frostLightTheme';
import { CssBaseline } from '@mui/material';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';
import { isColorSchemeDetectionSupported, isDarkModePreferred } from '../utils/helpers';

// Toggle the theme based on whether the company is "rh" or "frost"
const app_co = process.env.REACT_APP_COMPANY;

export const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useBaseContext must be used within a BaseContextProvider');
  }
  return context;
};

export const BaseThemeContextProvider = ({ user, children }) => {
  const prefersDarkMode = isDarkModePreferred();
  const [isDarkMode, setIsDarkMode] = useState(prefersDarkMode);
  const theme = useMemo(
    () => createTheme(
      isDarkMode ?
      (app_co === "frost" ? frostDarkTheme : darkTheme) :
      (app_co === "frost" ? frostLightTheme : lightTheme)
  ), [isDarkMode]);
  const { renderSnackBarAlertComponent, addAlertMessages } = useSnackBarAlertMessages();

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  // This useEffect is created to handle the live update of the device's preferences
  // We also need to handle this differently when there is a logged in user
  useEffect(() => {
    const handleChange = (event) => {
      const preferedDarkMode = event.matches;
      // if there is an logged in user, we'll handle that check and theme change in the ThemeContext.js
      // as we need to check some user attributes first
      if (user) {
        setIsDarkMode(preferedDarkMode);
        return;
      }
      // if no logged in user(guest), we'll check if the change in the preference is different than the current state
      // and change the theme accordingly to that
      if ((!preferedDarkMode && isDarkMode) || (preferedDarkMode && !isDarkMode)) {
        toggleTheme();
      }
    };

    // if the feature is supported, we'll set up the listener
    if (isColorSchemeDetectionSupported()) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      mediaQuery.addEventListener('change', handleChange);

      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
    // If the color scheme detection is not supported, we don't need to do anything in the cleanup
    return () => {};
  }, [isDarkMode, user]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        isDarkMode,
        toggleTheme,
        addAlertMessages,
      }}
    >
      <ThemeProvider theme={theme}>
        {renderSnackBarAlertComponent()}
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
